import { useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import CollapseSlot from './CollapseSlot'
import SelectionCheckBadge from './SelectionCheckBadge'
import {
  TRUST_INSPECTION_47,
  TRUST_INSPECTION_47_TOTAL,
  trustInspectionCategoryTitleKey,
  trustInspectionPointKey,
  trustInspectionPointNumber,
  type TrustInspectionCategoryId,
} from '@redevicer/shared'

export default function TrustInspection47() {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<TrustInspectionCategoryId | null>(null)
  const [checklistOpen, setChecklistOpen] = useState(false)

  function toggleCategory(id: TrustInspectionCategoryId) {
    setOpenId((current) => (current === id ? null : id))
  }

  const comingSoon = true
  const showFullChecklist = !comingSoon || checklistOpen

  function toggleChecklist() {
    if (!comingSoon) return
    setChecklistOpen((open) => {
      if (open) setOpenId(null)
      return !open
    })
  }

  const statLabel = t('trust.inspection47.statLabel', { count: TRUST_INSPECTION_47_TOTAL })

  const statBlock = comingSoon ? (
    <button
      type="button"
      className="vc-inspection47__stat vc-inspection47__stat-trigger"
      aria-label={statLabel}
      aria-expanded={checklistOpen}
      aria-controls="vc-inspection47-checklist"
      onClick={toggleChecklist}
    >
      <span className="vc-inspection47__stat-body">
        <span className="vc-inspection47__stat-num">{TRUST_INSPECTION_47_TOTAL}</span>
        <span className="vc-inspection47__stat-label">{t('trust.inspection47.statShort')}</span>
      </span>
      <span
        className={`vc-inspection47__stat-chev${checklistOpen ? ' vc-inspection47__stat-chev--open' : ''}`}
        aria-hidden
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  ) : (
    <div className="vc-inspection47__stat" aria-label={statLabel}>
      <span className="vc-inspection47__stat-num">{TRUST_INSPECTION_47_TOTAL}</span>
      <span className="vc-inspection47__stat-label">{t('trust.inspection47.statShort')}</span>
    </div>
  )

  return (
    <div
      className={`vc-inspection47${comingSoon && !showFullChecklist ? ' vc-inspection47--compact' : ''}${showFullChecklist ? ' vc-inspection47--expanded' : ''}`}
      aria-labelledby="vc-inspection47-title"
      aria-describedby={comingSoon && showFullChecklist ? 'vc-inspection47-soon' : undefined}
    >
      <div className="vc-inspection47__hero">
        <div className="vc-inspection47__eyebrow-row">
          <p className="vc-inspection47__eyebrow">{t('trust.inspection47.eyebrow')}</p>
          {comingSoon ? (
            <span className="vc-inspection47__soon">{t('trust.inspection47.comingSoonBadge')}</span>
          ) : null}
        </div>
        <div className="vc-inspection47__headline">
          {statBlock}
          <div className="vc-inspection47__headline-text">
            <h4 id="vc-inspection47-title" className="vc-inspection47__title">
              {t('trust.inspection47.title')}
            </h4>
            <p className="vc-inspection47__meta">{t('trust.inspection47.meta')}</p>
          </div>
        </div>
      </div>

      <CollapseSlot
        open={showFullChecklist}
        className="vc-inspection47__details"
        classNameWhenOpen="vc-inspection47__details--open"
        ariaHiddenWhenClosed
      >
        <div className="vc-inspection47__details-body">
          <p className="vc-inspection47__lead">{t('trust.inspection47.lead')}</p>
          {comingSoon ? (
            <p id="vc-inspection47-soon" className="vc-inspection47__coming-soon-note">
              {t('trust.inspection47.comingSoonNotice')}
            </p>
          ) : null}
          <div id="vc-inspection47-checklist" className="vc-inspection47__list">
            {TRUST_INSPECTION_47.map((cat, catIdx) => {
              const open = openId === cat.id
              return (
                <article
                  key={cat.id}
                  className={`vc-inspection47__cat${open ? ' vc-inspection47__cat--open' : ''}`}
                  style={{ '--cat-i': catIdx } as CSSProperties}
                >
                  <button
                    type="button"
                    className="vc-inspection47__cat-head"
                    aria-expanded={open}
                    onClick={() => toggleCategory(cat.id)}
                  >
                    <span className="vc-inspection47__letter" aria-hidden="true">
                      {cat.letter}
                    </span>
                    <span className="vc-inspection47__cat-title">{t(trustInspectionCategoryTitleKey(cat.id))}</span>
                    <span className="vc-inspection47__cat-count">{cat.pointIds.length}</span>
                    <span className="vc-inspection47__toggle" aria-hidden="true" />
                  </button>
                  <div className={`vc-inspection47__panel${open ? ' vc-inspection47__panel--open' : ''}`}>
                    <div className="vc-inspection47__panel-inner">
                      <ol className="vc-inspection47__points">
                        {cat.pointIds.map((pointId, pointIdx) => (
                          <li
                            key={pointId}
                            className="vc-inspection47__point"
                            style={{ '--point-i': pointIdx } as CSSProperties}
                          >
                            <span className="vc-inspection47__point-num">
                              {trustInspectionPointNumber(cat.id, pointIdx)}
                            </span>
                            <span className="vc-inspection47__point-text">{t(trustInspectionPointKey(pointId))}</span>
                            <SelectionCheckBadge accent="#0f766e" />
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      </CollapseSlot>
    </div>
  )
}
